import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { MUSCLE_LABELS } from "../components/BodyDiagram";
import AnatomyViewer from "../components/anatomy/AnatomyViewer";
import { canonicalMuscleId, MUSCLE_BY_ID } from "../data/muscleAtlas";
import { ArrowLeft, Flame, TrendingDown, TrendingUp, Activity, X, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter W — Body Chart Timeline & Cross-Session Heatmap.
 *
 * Renders the full BodyDiagram with a heatmap overlay summarizing how many
 * sessions touched each muscle, recoloured by clinical trend. Tapping a
 * muscle opens a drilldown drawer showing every session where it was worked,
 * pain/ROM changes, and a quick "open session" link.
 *
 * This is intended to become the central clinical surface of Posturra —
 * future iterations (anatomy tabs, client portal) layer on top of this
 * heatmap. The endpoint payload is shared across all those surfaces.
 */
export default function BodyChartHistory() {
  const { id: clientId } = useParams();
  const [client, setClient] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMuscle, setActiveMuscle] = useState(null);
  const [legendOpen, setLegendOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/clients/${clientId}`),
      api.get(`/clients/${clientId}/body-chart-timeline`),
    ])
      .then(([c, t]) => {
        if (cancelled) return;
        setClient(c.data);
        setData(t.data);
      })
      .catch(() => toast.error("Could not load body chart timeline"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const heatmapRaw = data?.heatmap || {};
  // Iter X — Normalize legacy session worked_areas ids onto the canonical
  // catalog ids the new AnatomyViewer expects (handles old shorthand like
  // 'upper_trap', 'ql', 'glute_med', etc.). The legacy BodyDiagram displays
  // still use the original ids for backward compatibility.
  const heatmap = useMemo(() => {
    const out = { ...heatmapRaw };
    for (const [k, v] of Object.entries(heatmapRaw)) {
      const canon = canonicalMuscleId(k);
      if (canon && canon !== k && !out[canon]) {
        out[canon] = v;
      }
    }
    return out;
  }, [heatmapRaw]);
  const muscles = data?.muscles || [];
  const summary = data?.summary || {};
  const recurring = data?.recurring_muscles || [];

  // For the muscle drilldown we want the active muscle resolved against the
  // payload's muscles list (which is keyed off the session.worked_areas ids).
  const activeMuscleData = useMemo(
    () => muscles.find((m) => m.muscle_id === activeMuscle || canonicalMuscleId(m.muscle_id) === activeMuscle) || null,
    [muscles, activeMuscle]
  );

  if (loading) {
    return (
      <Layout>
        <div className="py-20 text-center" data-testid="body-chart-loading">
          <div className="inline-block w-12 h-12 rounded-full border-4 border-sage-500 border-t-transparent animate-spin mb-4" />
          <p className="text-ink-700">Loading body chart timeline…</p>
        </div>
      </Layout>
    );
  }

  const hasData = (summary.total_sessions || 0) > 0;
  const hasHeatmap = Object.keys(heatmap).length > 0;

  return (
    <Layout>
      <Link
        to={`/clients/${clientId}`}
        className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900 mb-6"
        data-testid="back-to-client"
      >
        <ArrowLeft className="w-4 h-4" /> Back to {client?.name || "client"}
      </Link>

      {/* Hero header */}
      <div className="mb-6">
        <div className="label-overline text-sage-600 mb-2">Body chart · treatment history</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900">
          {client?.name || "Client"}&rsquo;s body chart
        </h1>
        <p className="text-ink-700 mt-2 max-w-2xl">
          A living visual of every muscle worked across {summary.total_sessions || 0} session
          {summary.total_sessions === 1 ? "" : "s"}. Tap any muscle to see its session-by-session
          history.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="body-chart-kpis">
        <Kpi label="Sessions" value={summary.total_sessions || 0} testid="kpi-sessions" />
        <Kpi label="Unique muscles" value={summary.unique_muscles_worked || 0} testid="kpi-muscles" />
        <Kpi
          label="Recurring areas"
          value={summary.recurring_muscle_count || 0}
          hint="worked ≥3 sessions"
          accent="terracotta"
          testid="kpi-recurring"
        />
        <Kpi
          label="Improved sessions"
          value={summary.improved_session_count || 0}
          hint="pain reduced ≥1pt"
          accent="sage"
          testid="kpi-improved"
        />
      </div>

      {!hasData && (
        <div
          className="bg-white border border-paper-200 rounded-3xl p-10 text-center"
          data-testid="body-chart-empty"
        >
          <p className="text-ink-700">No completed sessions yet. Complete a session and come back — every muscle you mark as worked will appear here, color-graded by frequency and trend.</p>
        </div>
      )}

      {hasData && !hasHeatmap && (
        <div
          className="bg-white border border-paper-200 rounded-3xl p-8 text-center"
          data-testid="body-chart-no-areas"
        >
          <p className="text-ink-700">{summary.total_sessions} completed session{summary.total_sessions === 1 ? "" : "s"}, but no muscles were tagged as worked. Use the Diagram tab during your next session to start building the heatmap.</p>
        </div>
      )}

      {hasHeatmap && (
        <>
          {/* Recurring areas chips — easy entry to drilldown */}
          {recurring.length > 0 && (
            <div className="mb-6" data-testid="recurring-chips">
              <div className="label-overline text-terracotta-600 mb-2">Recurring problem areas (worked ≥3 sessions)</div>
              <div className="flex flex-wrap gap-2">
                {recurring.map((mid) => {
                  const h = heatmap[mid] || {};
                  return (
                    <button
                      key={mid}
                      type="button"
                      onClick={() => setActiveMuscle(mid)}
                      data-testid={`recurring-chip-${mid}`}
                      className="min-h-[40px] px-4 rounded-full border border-terracotta-500/30 bg-terracotta-500/5 text-terracotta-700 hover:bg-terracotta-500/10 text-sm font-medium inline-flex items-center gap-2 transition-all"
                    >
                      <Flame className="w-3.5 h-3.5" />
                      {MUSCLE_LABELS[mid] || MUSCLE_BY_ID[canonicalMuscleId(mid)]?.label || mid}
                      <span className="text-xs opacity-70">×{h.count || 0}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Legend */}
          <HeatmapLegend
            open={legendOpen}
            onToggle={() => setLegendOpen((v) => !v)}
          />

          {/* The realistic anatomy viewer with layered overlays */}
          <div className="bg-white border border-paper-200 rounded-3xl p-4 sm:p-6 mb-6" data-testid="body-chart-canvas">
            <AnatomyViewer
              heatmap={heatmap}
              sex={(data?.client_sex === "f" || data?.client_sex === "female") ? "f" : "m"}
              selectedMuscleId={activeMuscle}
              onMuscleSelect={(mid) => setActiveMuscle(mid)}
              clientId={clientId}
            />
          </div>

          {/* Ranked muscle list — secondary entry */}
          <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="label-overline text-sage-600 mb-1">Treatment frequency</div>
                <h2 className="font-heading text-xl text-ink-900">All muscles worked</h2>
              </div>
              <div className="text-xs text-ink-500">Tap a row for the full session history</div>
            </div>
            <ul className="divide-y divide-paper-100" data-testid="muscle-frequency-list">
              {muscles.map((m) => (
                <li key={m.muscle_id}>
                  <button
                    type="button"
                    onClick={() => setActiveMuscle(m.muscle_id)}
                    data-testid={`muscle-row-${m.muscle_id}`}
                    className="w-full text-left flex items-center gap-4 py-3 hover:bg-paper-50 rounded-xl px-3 transition-colors"
                  >
                    <span className="flex-1 text-sm font-medium text-ink-900">
                      {MUSCLE_LABELS[m.muscle_id] || MUSCLE_BY_ID[canonicalMuscleId(m.muscle_id)]?.label || m.muscle_id}
                    </span>
                    <TrendBadge trend={(heatmap[m.muscle_id] || {}).trend} />
                    <span className="text-sm text-ink-500">×{m.count}</span>
                    <ChevronRight className="w-4 h-4 text-ink-400" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Drilldown drawer */}
      {activeMuscleData && (
        <MuscleDrilldown
          clientId={clientId}
          muscle={activeMuscleData}
          heat={heatmap[activeMuscleData.muscle_id]}
          onClose={() => setActiveMuscle(null)}
        />
      )}
    </Layout>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────
function Kpi({ label, value, hint, accent = "ink", testid }) {
  const accentText = accent === "sage" ? "text-sage-700" : accent === "terracotta" ? "text-terracotta-700" : "text-ink-900";
  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-4" data-testid={testid}>
      <div className="label-overline text-ink-500">{label}</div>
      <div className={`font-heading text-3xl mt-1 ${accentText}`}>{value}</div>
      {hint && <div className="text-xs text-ink-500 mt-1">{hint}</div>}
    </div>
  );
}

function TrendBadge({ trend }) {
  if (!trend || trend === "unknown") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-ink-500 bg-paper-100 px-2 py-0.5 rounded-full">
        —
      </span>
    );
  }
  if (trend === "improving") {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-sage-700 bg-sage-500/10 px-2 py-0.5 rounded-full"
        data-testid="trend-improving"
      >
        <TrendingDown className="w-3 h-3" /> improving
      </span>
    );
  }
  if (trend === "worse") {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-terracotta-700 bg-terracotta-500/10 px-2 py-0.5 rounded-full"
        data-testid="trend-worse"
      >
        <TrendingUp className="w-3 h-3" /> worsening
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-500/10 px-2 py-0.5 rounded-full"
      data-testid="trend-stable"
    >
      <Activity className="w-3 h-3" /> holding
    </span>
  );
}

function HeatmapLegend({ open, onToggle }) {
  return (
    <div className="bg-paper-50 border border-paper-200 rounded-2xl p-4 mb-6" data-testid="heatmap-legend">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left"
        data-testid="heatmap-legend-toggle"
      >
        <span className="label-overline text-ink-700">Reading the heatmap</span>
        <span className="text-xs text-ink-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
          <LegendSwatch label="Improving" hint="Pain trended down" colorCls="bg-sage-500" />
          <LegendSwatch label="Holding" hint="No net change" colorCls="bg-amber-500" />
          <LegendSwatch label="Worsening" hint="Pain trended up" colorCls="bg-terracotta-500" />
          <LegendSwatch label="Recurring" hint="Worked ≥3 sessions · subtle ring" colorCls="bg-ink-900" ringed />
        </div>
      )}
    </div>
  );
}

function LegendSwatch({ label, hint, colorCls, ringed = false }) {
  return (
    <div className="flex items-start gap-2">
      <span
        className={`w-4 h-4 rounded-full shrink-0 ${colorCls} ${ringed ? "ring-2 ring-offset-1 ring-ink-700/30" : ""}`}
      />
      <div>
        <div className="font-medium text-ink-900">{label}</div>
        <div className="text-ink-500">{hint}</div>
      </div>
    </div>
  );
}

function MuscleDrilldown({ clientId, muscle, heat, onClose }) {
  // Tap-outside or Escape closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const label = MUSCLE_LABELS[muscle.muscle_id] || MUSCLE_BY_ID[canonicalMuscleId(muscle.muscle_id)]?.label || muscle.muscle_id;
  const sessions = muscle.sessions || [];
  const firstDate = muscle.first_worked ? new Date(muscle.first_worked).toLocaleDateString() : "—";
  const lastDate = muscle.last_worked ? new Date(muscle.last_worked).toLocaleDateString() : "—";

  return (
    <div
      className="fixed inset-0 z-40 flex items-stretch justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="muscle-drilldown"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink-900/30 backdrop-blur-sm"
        data-testid="muscle-drilldown-backdrop"
      />
      <div className="relative w-full sm:w-[min(560px,90vw)] bg-white border-l border-paper-200 shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-paper-200 p-5 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="label-overline text-sage-600 mb-1">Muscle history</div>
            <h2 className="font-heading text-2xl text-ink-900 truncate" data-testid="muscle-drilldown-title">{label}</h2>
            <div className="text-xs text-ink-500 mt-1">
              Worked {muscle.count}× · first {firstDate} · last {lastDate}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="muscle-drilldown-close"
            className="w-9 h-9 rounded-full hover:bg-paper-100 inline-flex items-center justify-center shrink-0"
            aria-label="Close drawer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Trend + recurring badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <TrendBadge trend={heat?.trend} />
            {muscle.count >= 3 && (
              <span className="inline-flex items-center gap-1 text-xs text-terracotta-700 bg-terracotta-500/10 px-2 py-0.5 rounded-full" data-testid="muscle-drilldown-recurring-badge">
                <Flame className="w-3 h-3" /> Recurring
              </span>
            )}
            {heat?.intensity != null && (
              <span className="text-xs text-ink-500">
                Heatmap intensity {Math.round(heat.intensity * 100)}%
              </span>
            )}
          </div>

          {/* Session-by-session */}
          <div>
            <div className="label-overline text-ink-700 mb-2">Sessions where this was worked</div>
            <ol className="space-y-2" data-testid="muscle-drilldown-sessions">
              {sessions.map((s) => {
                const dateStr = s.date ? new Date(s.date).toLocaleDateString() : "—";
                const dot =
                  s.color === "green"
                    ? "bg-sage-500"
                    : s.color === "rose"
                    ? "bg-terracotta-500"
                    : s.color === "amber"
                    ? "bg-amber-500"
                    : "bg-paper-300";
                return (
                  <li
                    key={s.session_id}
                    className="bg-paper-50 border border-paper-200 rounded-2xl p-3"
                    data-testid={`muscle-session-row-${s.session_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`w-2.5 h-2.5 rounded-full ${dot} mt-2 shrink-0`} aria-hidden="true" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="text-sm font-medium text-ink-900">Session {s.session_num}</div>
                          <div className="text-xs text-ink-500 inline-flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" /> {dateStr}
                          </div>
                        </div>
                        {(s.pain_before != null || s.pain_after != null) && (
                          <div className="text-xs text-ink-700 mt-1">
                            Pain {s.pain_before ?? "—"} → {s.pain_after ?? "—"}
                            {s.pain_delta != null && s.pain_delta !== 0 && (
                              <span className={`ml-2 font-medium ${s.pain_delta > 0 ? "text-sage-700" : "text-terracotta-700"}`}>
                                {s.pain_delta > 0 ? `−${s.pain_delta}` : `+${Math.abs(s.pain_delta)}`} pts
                              </span>
                            )}
                          </div>
                        )}
                        {(s.techniques_used || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {s.techniques_used.slice(0, 5).map((t) => (
                              <span key={t} className="text-[10px] uppercase tracking-[0.1em] bg-white border border-paper-200 rounded-full px-2 py-0.5 text-ink-700">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {s.headline && (
                          <div className="text-xs text-ink-500 italic mt-2 line-clamp-2">{s.headline}</div>
                        )}
                        <Link
                          to={`/clients/${clientId}/sessions/${s.session_id}`}
                          className="inline-block text-xs text-sage-700 hover:text-sage-800 underline mt-2"
                          data-testid={`muscle-session-link-${s.session_id}`}
                        >
                          Open session
                        </Link>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <Link
            to={`/clients/${clientId}/timeline`}
            className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full bg-ink-900 hover:bg-ink-700 text-white text-sm font-medium"
            data-testid="muscle-drilldown-open-timeline"
          >
            Open full clinical timeline <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
